import React, { useMemo } from "react";
import { View, StyleSheet } from "react-native";
import { useNavigation, type NavigationProp } from "@react-navigation/native";
import { FileText } from "lucide-react-native";
import { Text } from "@src/packages/medical-history/components/MedicalHistoryText";
import {
  IconInput,
  QuestionLayout,
  SquareOptionButton,
} from "@src/packages/medical-history/components";
import { useTranslations } from "@src/packages/i18n";
import { useMenstrualHealth } from "../MenstrualHealthContext";
import type { MenstrualHealthState } from "../MenstrualHealthContext";
import { buildMenstrualHealthNavigationStack } from "../buildMenstrualHealthNavigationStack";
import { FLOW_INTENSITY_OPTIONS, SYMPTOM_OPTIONS } from "../options";
import type { FlowIntensity } from "../options";
import type { MenstrualHealthStackParamList } from "../MenstrualHealthNavigator";
import { useMedicalHistoryTheme } from "../../../theme/MedicalHistoryTheme";

export function SymptomsScreen() {
  const t = useTranslations();
  const { state, updateState, save } = useMenstrualHealth();
  const theme = useMedicalHistoryTheme();
  const navigation =
    useNavigation<NavigationProp<MenstrualHealthStackParamList>>();

  const periodDetails = useMemo(() => {
    const structuredSymptoms = state.symptoms
      .filter((symptom) => symptom.symptom !== "other")
      .map((symptom) => {
        const option = SYMPTOM_OPTIONS.find(
          (candidate) => candidate.id === symptom.symptom,
        );
        return option ? t(option.labelKey) : null;
      });
    const otherDetails = state.symptoms.find(
      (symptom) => symptom.symptom === "other",
    )?.other;

    return [...structuredSymptoms, otherDetails]
      .filter((value): value is string => !!value?.trim())
      .join("\n\n");
  }, [state.symptoms, t]);

  const buildDetailsUpdate = (value: string): Partial<MenstrualHealthState> => {
    const normalizedValue = value.trim();
    const existingOther = state.symptoms.find(
      (symptom) => symptom.symptom === "other",
    );
    const idsToDelete = [
      ...state.symptomIdsToDelete,
      ...state.symptoms
        .filter(
          (symptom) => symptom.symptom !== "other" || !normalizedValue.length,
        )
        .map((symptom) => symptom.id)
        .filter((id): id is string => !!id),
    ];

    return {
      hasPeriodSymptoms: normalizedValue.length > 0,
      symptoms: normalizedValue
        ? [
            {
              ...(existingOther ?? { symptom: "other" as const }),
              symptom: "other" as const,
              other: normalizedValue,
            },
          ]
        : [],
      symptomIdsToDelete: [...new Set(idsToDelete)],
    };
  };

  const handleDetailsChange = (value: string) => {
    updateState(buildDetailsUpdate(value));
  };

  const handleFlowSelect = (intensity: FlowIntensity) => {
    updateState({ menstrualFlowIntensity: intensity });
  };

  const handleSave = async () => {
    const updates = {
      ...buildDetailsUpdate(periodDetails),
      menstrualFlowIntensity: state.menstrualFlowIntensity,
    };
    updateState(updates);
    await save(updates);
    const stack = buildMenstrualHealthNavigationStack({ ...state, ...updates });
    const currentIdx = stack.indexOf("Symptoms");
    const nextRoute = stack[currentIdx + 1];
    if (nextRoute) {
      navigation.navigate(nextRoute);
    }
  };

  return (
    <QuestionLayout
      title={t("med_history.gynae.menstrual_health.symptoms_title")}
      subtitle={t("med_history.gynae.menstrual_health.symptoms_subtitle")}
      onBack={() => navigation.goBack()}
      onSave={handleSave}
      saveLabel={t("med_history.gynae.menstrual_health.continue")}
    >
      <View style={styles.container}>
        <View style={styles.flowSection}>
          <Text
            style={[styles.sectionLabel, { color: theme.cardLabel }]}
            className="pl-1 text-sm font-medium tracking-wide"
          >
            {t("med_history.gynae.menstrual_health.flow_intensity_label")}
          </Text>
          <View style={styles.flowRow}>
            {FLOW_INTENSITY_OPTIONS.map((option) => (
              <SquareOptionButton
                key={option.id}
                icon={option.icon}
                label={t(option.labelKey)}
                isSelected={state.menstrualFlowIntensity === option.id}
                onPress={() => handleFlowSelect(option.id)}
                labelSize="sm"
              />
            ))}
          </View>
        </View>
        <IconInput
          label={t("med_history.gynae.menstrual_health.symptoms_details_label")}
          icon={FileText}
          placeholder={t(
            "med_history.gynae.menstrual_health.symptoms_details_placeholder",
          )}
          value={periodDetails}
          onChangeText={handleDetailsChange}
          multiline
          numberOfLines={7}
          autoCapitalize="sentences"
        />
      </View>
    </QuestionLayout>
  );
}

const styles = StyleSheet.create({
  container: {
    width: "100%",
    marginBottom: 40,
  },
  flowSection: {
    marginBottom: 24,
  },
  sectionLabel: {
    color: "#8B8178",
    marginBottom: 12,
  },
  flowRow: {
    flexDirection: "row",
    gap: 12,
    width: "100%",
  },
});
